import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import map from 'lodash/map';
import { useSelector } from 'react-redux';
import { Dropdown } from '@yme/atoms/dropdown';
import { Input } from '@yme/atoms/input';
import { ProductProperties } from '@yme/atoms/product-properties';
import { LinkStyled } from '@yme/components/Header/styled';
import { Link } from '@yme/components/Link';
import { ImageWrapper } from '@yme/components/ShoppingCart/CartItem/styled';
import { getRTL } from '@yme/selectors/index';
import { useTranslation } from '@yme/services/i18n';
import { colorCoreBlack300 } from '@yme/tokens/colors';
import { FlexWrapper } from '@yme/tokens/flexTokens';
import { HeadingLevel3, RegularTextLevel3, RegularTextLevel5 } from '@yme/tokens/typography';
import { formatPrice } from '@yme/utils/formatPrice';
import { getEurosFromCentAmount } from '../helpers';
import { ContainerStyled, SectionContainerStyled, QuantityContainerStyled, } from './ProductItemMobileStyled';
import { OtherReasonInputContainerStyled } from './ProductItemStyled';
export const ProductItemMobile = memo(({ sku, name, image, colourName, quantity, price, size, returnReasons, returnReasonCode, returnReasonText, returnReasonOther, returnQuantity, isReadOnly, onReasonChange, onOtherReasonTextChange, onQuantityChange, }) => {
    const isRightToLeft = useSelector(getRTL);
    const { language, t } = useTranslation();
    const isOtherReason = returnReasonCode === 5;
    const [otherReasonText, setOtherReasonText] = useState(returnReasonOther);
    useEffect(() => {
        setOtherReasonText(returnReasonOther);
    }, [returnReasonOther]);
    const handleOtherReasonTextChange = useCallback((value) => {
        onOtherReasonTextChange?.(sku, value);
        setOtherReasonText(value);
    }, [onOtherReasonTextChange, sku]);
    const reasonDDItems = useMemo(() => map(returnReasons, (reasonText, reasonCode) => ({
        value: reasonCode,
        label: reasonText,
    })), [returnReasons]);
    const quantityDDItems = useMemo(() => map(new Array(quantity + 1), (item, index) => ({
        value: String(index),
        label: String(index),
    })), [quantity]);
    return (React.createElement(ContainerStyled, null,
        React.createElement(FlexWrapper, { mb: "16px" },
            React.createElement(ImageWrapper, { isRightToLeft: false, isImageError: !true },
                React.createElement(Link, { to: {
                        pathname: image,
                        hash: sku,
                    } },
                    React.createElement("img", { src: image, alt: name, loading: "lazy" }))),
            React.createElement(FlexWrapper, { flexDirection: "column" },
                React.createElement(RegularTextLevel5, { color: colorCoreBlack300 }, sku),
                React.createElement(LinkStyled, { to: "" },
                    React.createElement(RegularTextLevel3, { weight: 500 }, name)),
                React.createElement(ProductProperties, { properties: {
                        color: colourName,
                        size,
                    } }))),
        React.createElement(SectionContainerStyled, null, isReadOnly ? (React.createElement(FlexWrapper, { justifyContent: "space-between" },
            React.createElement(RegularTextLevel3, { color: colorCoreBlack300 }, t('returnForm_selectItemsReasonColumn_header')),
            React.createElement(FlexWrapper, { flexDirection: "column", textAlign: "end", ml: "24px" },
                React.createElement(RegularTextLevel3, null, returnReasonOther ? returnReasonOther : returnReasonText)))) : (React.createElement(React.Fragment, null,
            React.createElement(Dropdown, { id: `Reason_${sku}`, title: t('returnForm_selectItemsReason_placeholder'), useForMobile: true, items: reasonDDItems, activeItem: returnReasonCode ? String(returnReasonCode) : '', isRightToLeft: isRightToLeft, inputPlaceholder: t('returnForm_selectItemsReason_placeholder'), hasRadioButton: true, onChange: (reasonCode, reasonText) => {
                    onReasonChange?.(sku, Number(reasonCode), reasonText);
                } }),
            isOtherReason && (React.createElement(OtherReasonInputContainerStyled, null,
                React.createElement(Input, { isTextarea: true, name: name, value: otherReasonText, changeHandler: handleOtherReasonTextChange })))))),
        React.createElement(SectionContainerStyled, null,
            React.createElement(FlexWrapper, { justifyContent: "space-between", alignItems: "center" },
                React.createElement(RegularTextLevel3, { color: colorCoreBlack300 }, t('returnForm_selectItemsQtyColumn_header')),
                isReadOnly ? (React.createElement(RegularTextLevel3, null, String(returnQuantity))) : (React.createElement(QuantityContainerStyled, null,
                    React.createElement(Dropdown, { useForMobile: true, id: `Quantity_${sku}`, title: t('Select quantity'), items: quantityDDItems, activeItem: String(returnQuantity), isRightToLeft: isRightToLeft, hasRadioButton: true, onChange: (value) => {
                            onQuantityChange?.(sku, value);
                        } }))))),
        React.createElement(FlexWrapper, { justifyContent: "space-between", alignItems: "center" },
            React.createElement(RegularTextLevel3, { color: colorCoreBlack300 }, t('returnForm_selectItemsTotalPaidColumn_header')),
            React.createElement(FlexWrapper, { flexDirection: "column", alignItems: "flex-end" },
                React.createElement(HeadingLevel3, null, formatPrice({
                    locale: language,
                    currencyName: price.currencyCode,
                    price: getEurosFromCentAmount(price.centAmount) * returnQuantity,
                })),
                React.createElement(RegularTextLevel5, { color: colorCoreBlack300 }, `${returnQuantity} x ${formatPrice({
                    locale: language,
                    currencyName: price.currencyCode,
                    price: getEurosFromCentAmount(price.centAmount),
                })}`)))));
});
ProductItemMobile.displayName = 'ProductItem';
